import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { AppDrawer } from '../ui/AppDrawer';
import { Plus, Loader2, MessageSquare, Calendar, User, AlertTriangle, CheckCircle2, Circle, Clock, Ban, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CFG = {
  todo:        { label: 'To Do',        icon: Circle,        color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  in_progress: { label: 'In Progress',  icon: Clock,         color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  done:        { label: 'Done',         icon: CheckCircle2,  color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  blocked:     { label: 'Blocked',      icon: Ban,           color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const PRIORITY_CFG = {
  low:    { label: 'Low',    color: 'text-slate-500', dot: 'bg-slate-400' },
  normal: { label: 'Normal', color: 'text-foreground', dot: 'bg-blue-500' },
  high:   { label: 'High',   color: 'text-amber-600', dot: 'bg-amber-500' },
  urgent: { label: 'Urgent', color: 'text-red-600', dot: 'bg-red-500' },
};

const CATEGORY_CFG = {
  general:        { label: 'General' },
  follow_up:      { label: 'Follow-Up' },
  permit:         { label: 'Permit' },
  inspection:     { label: 'Inspection' },
  material_order: { label: 'Material Order' },
  closeout:       { label: 'Closeout' },
};

const ANCHOR_OPTIONS = [
  { value: 'phase_start', label: 'Phase Start' },
  { value: 'phase_end',   label: 'Phase End' },
  { value: 'job_start',   label: 'Job Start' },
  { value: 'job_end',     label: 'Job End' },
];

export function ProjectTasks({ projectId, canEdit, teamUsers }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', status: 'todo', priority: 'normal',
    assigned_to: '', due_date: '', category: '', schedule_item_id: '',
    due_date_mode: 'manual', date_offset_anchor: '', date_offset_days: '',
  });
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [scheduleItems, setScheduleItems] = useState([]);

  const fetchTasks = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await projectsAPI.listTasks(projectId, params);
      setTasks(res.data.tasks || []);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [projectId, statusFilter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Fetch schedule items for linking
  useEffect(() => {
    projectsAPI.listSchedule(projectId).then(res => {
      setScheduleItems(res.data.items || []);
    }).catch(() => {});
  }, [projectId]);

  const openTask = async (task) => {
    setSelectedTask(task);
    setForm({
      title: task.title, description: task.description || '', status: task.status,
      priority: task.priority, assigned_to: task.assigned_to || '', due_date: task.due_date || '',
      category: task.category || '', schedule_item_id: task.schedule_item_id || '',
      due_date_mode: task.due_date_mode || 'manual',
      date_offset_anchor: task.date_offset_anchor || '',
      date_offset_days: task.date_offset_days != null ? String(task.date_offset_days) : '',
    });
    try {
      const res = await projectsAPI.listTaskComments(projectId, task.id);
      setComments(res.data.comments || []);
    } catch { setComments([]); }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Task title is required'); return; }
    setSaving(true);
    try {
      await projectsAPI.createTask(projectId, {
        title: form.title.trim(),
        description: form.description || undefined,
        status: form.status,
        priority: form.priority,
        category: form.category || undefined,
        assigned_to: form.assigned_to || undefined,
        due_date: form.due_date || undefined,
        due_date_mode: form.due_date_mode || 'manual',
        date_offset_anchor: form.due_date_mode === 'auto' ? form.date_offset_anchor || undefined : undefined,
        date_offset_days: form.due_date_mode === 'auto' && form.date_offset_days ? parseInt(form.date_offset_days) : undefined,
        schedule_item_id: form.schedule_item_id || undefined,
      });
      toast.success('Task created');
      setDrawerOpen(false);
      resetForm();
      fetchTasks();
    } catch { toast.error('Failed to create task'); } finally { setSaving(false); }
  };

  const resetForm = () => {
    setForm({ title: '', description: '', status: 'todo', priority: 'normal', assigned_to: '', due_date: '', category: '', schedule_item_id: '', due_date_mode: 'manual', date_offset_anchor: '', date_offset_days: '' });
  };

  const handleUpdate = async (field, value) => {
    if (!selectedTask) return;
    try {
      const payload = { [field]: value };
      await projectsAPI.updateTask(projectId, selectedTask.id, payload);
      setSelectedTask(prev => ({ ...prev, [field]: value }));
      fetchTasks();
    } catch { toast.error('Failed to update task'); }
  };

  const handleStatusToggle = async (task, e) => {
    e.stopPropagation();
    const newStatus = task.status === 'done' ? 'todo' : 'done';
    try {
      await projectsAPI.updateTask(projectId, task.id, { status: newStatus });
      fetchTasks();
    } catch { toast.error('Failed to update task'); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    try {
      const res = await projectsAPI.addTaskComment(projectId, selectedTask.id, { content: newComment.trim() });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await projectsAPI.deleteTask(projectId, taskId);
      setSelectedTask(null);
      fetchTasks();
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete task'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const counts = { all: tasks.length, todo: 0, in_progress: 0, done: 0, blocked: 0 };
  tasks.forEach(t => { if (counts[t.status] !== undefined) counts[t.status]++; });

  return (
    <div className="space-y-4" data-testid="project-tasks">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {['', 'todo', 'in_progress', 'done', 'blocked'].map(s => (
            <Button key={s} variant={statusFilter === s ? 'default' : 'ghost'} size="sm" onClick={() => setStatusFilter(s)} className="text-xs h-8" data-testid={`task-filter-${s || 'all'}`}>
              {s ? STATUS_CFG[s].label : 'All'} <span className="ml-1 text-xs opacity-60">({s ? counts[s] : counts.all})</span>
            </Button>
          ))}
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => { setSelectedTask(null); resetForm(); setDrawerOpen(true); }} data-testid="new-task-btn">
            <Plus className="w-4 h-4 mr-1" /> New Task
          </Button>
        )}
      </div>

      {/* Task List — keep this clean */}
      {tasks.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="tasks-empty">
          <p className="text-lg font-medium">No tasks yet</p>
          <p className="text-sm mt-1">Create tasks to track work on this project.</p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y" data-testid="tasks-list">
          {tasks.map(task => {
            const sCfg = STATUS_CFG[task.status] || STATUS_CFG.todo;
            const pCfg = PRIORITY_CFG[task.priority] || PRIORITY_CFG.normal;
            const isOverdue = task.due_date && task.status !== 'done' && new Date(task.due_date) < new Date();
            const StatusIcon = sCfg.icon;
            const catLabel = task.category ? CATEGORY_CFG[task.category]?.label : null;
            return (
              <div key={task.id} onClick={() => openTask(task)} className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer group" data-testid={`task-row-${task.id}`}>
                <button onClick={e => handleStatusToggle(task, e)} className="flex-shrink-0" data-testid={`task-toggle-${task.id}`}>
                  <StatusIcon className={`w-5 h-5 ${task.status === 'done' ? 'text-green-500' : task.status === 'blocked' ? 'text-red-500' : 'text-muted-foreground hover:text-primary'}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${task.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{task.title}</p>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pCfg.dot}`} title={pCfg.label} />
                    {catLabel && <Badge variant="outline" className="text-[10px] h-4 flex-shrink-0">{catLabel}</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    {task.due_date && (
                      <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
                        {isOverdue && <AlertTriangle className="w-3 h-3" />}<Calendar className="w-3 h-3" />{task.due_date}
                        {task.due_date_mode === 'auto' && !task.due_date_overridden && <span className="text-[10px] opacity-60">(auto)</span>}
                      </span>
                    )}
                    {task.schedule_item_id && <span className="flex items-center gap-1"><Link2 className="w-3 h-3" />Phase</span>}
                    {task.comment_count > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{task.comment_count}</span>}
                  </div>
                </div>
                {/* Assignee */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {task.assignee_name ? (
                    <div className="flex items-center gap-1.5" data-testid={`task-assignee-${task.id}`}>
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">
                        {task.assignee_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <span className="text-xs text-muted-foreground hidden sm:inline max-w-[100px] truncate">{task.assignee_name}</span>
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center opacity-40" title="Unassigned">
                      <User className="w-3 h-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Task Drawer */}
      <AppDrawer open={drawerOpen && !selectedTask} onOpenChange={setDrawerOpen} title="New Task" description="Create a task for this project">
        <div className="space-y-4 p-1">
          <div><Label>Title *</Label><Input autoFocus value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="What needs to be done?" onKeyDown={e => e.key === 'Enter' && handleCreate()} data-testid="task-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} placeholder="Optional details..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({...f, priority: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_CFG).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category || 'none'} onValueChange={v => setForm(f => ({...f, category: v === 'none' ? '' : v}))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {Object.entries(CATEGORY_CFG).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} data-testid="task-due-date" />
            </div>
            <div>
              <Label>Assign To</Label>
              <Select value={form.assigned_to || 'unassigned'} onValueChange={v => setForm(f => ({...f, assigned_to: v === 'unassigned' ? '' : v}))}>
                <SelectTrigger data-testid="task-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {scheduleItems.length > 0 && (
            <div>
              <Label>Linked Phase</Label>
              <Select value={form.schedule_item_id || 'none'} onValueChange={v => setForm(f => ({...f, schedule_item_id: v === 'none' ? '' : v}))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {scheduleItems.map(si => <SelectItem key={si.id} value={si.id}>{si.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={handleCreate} disabled={saving} className="w-full" data-testid="create-task-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Create Task
          </Button>
        </div>
      </AppDrawer>

      {/* Task Detail Drawer */}
      <AppDrawer open={!!selectedTask} onOpenChange={(open) => { if (!open) setSelectedTask(null); }} title={selectedTask?.title || 'Task'} description={`#${selectedTask?.id?.slice(0,8)}`}>
        {selectedTask && (
          <div className="space-y-4 p-1">
            {/* Status & Priority */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => { setForm(f => ({...f, status: v})); handleUpdate('status', v); }}>
                  <SelectTrigger data-testid="task-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_CFG).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => { setForm(f => ({...f, priority: v})); handleUpdate('priority', v); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_CFG).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Category */}
            <div>
              <Label>Category</Label>
              <Select value={form.category || 'none'} onValueChange={v => { const val = v === 'none' ? '' : v; setForm(f => ({...f, category: val})); handleUpdate('category', val); }}>
                <SelectTrigger data-testid="task-detail-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {Object.entries(CATEGORY_CFG).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Assigned To */}
            <div>
              <Label>Assigned To</Label>
              <Select value={form.assigned_to || 'unassigned'} onValueChange={v => { const val = v === 'unassigned' ? '' : v; setForm(f => ({...f, assigned_to: val})); handleUpdate('assigned_to', val); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Assigned By & Date Assigned — read-only */}
            {(selectedTask.assigner_name || selectedTask.date_assigned) && (
              <div className="flex gap-4 text-xs text-muted-foreground bg-muted/30 rounded-md px-3 py-2" data-testid="task-assignment-info">
                {selectedTask.assigner_name && <span>Assigned by: <strong className="text-foreground">{selectedTask.assigner_name}</strong></span>}
                {selectedTask.date_assigned && <span>on {selectedTask.date_assigned}</span>}
              </div>
            )}

            {/* Due Date */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Due Date</Label>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">Auto</span>
                  <Switch
                    checked={form.due_date_mode === 'auto'}
                    onCheckedChange={v => {
                      const mode = v ? 'auto' : 'manual';
                      setForm(f => ({...f, due_date_mode: mode}));
                      handleUpdate('due_date_mode', mode);
                    }}
                    data-testid="task-auto-due-toggle"
                  />
                </div>
              </div>
              {form.due_date_mode === 'manual' ? (
                <Input type="date" value={form.due_date} onChange={e => { setForm(f => ({...f, due_date: e.target.value})); handleUpdate('due_date', e.target.value); }} data-testid="task-detail-due-date" />
              ) : (
                <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                  <p className="text-xs text-muted-foreground">Due date will be calculated from an anchor date plus an offset.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Anchor</Label>
                      <Select value={form.date_offset_anchor || 'none'} onValueChange={v => { const val = v === 'none' ? '' : v; setForm(f => ({...f, date_offset_anchor: val})); handleUpdate('date_offset_anchor', val); }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select...</SelectItem>
                          {ANCHOR_OPTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Offset (days)</Label>
                      <Input type="number" className="h-8 text-xs" value={form.date_offset_days} onChange={e => { setForm(f => ({...f, date_offset_days: e.target.value})); handleUpdate('date_offset_days', e.target.value ? parseInt(e.target.value) : 0); }} placeholder="0" />
                    </div>
                  </div>
                  {selectedTask.due_date && (
                    <p className="text-xs text-muted-foreground">Current due date: <strong>{selectedTask.due_date}</strong>{selectedTask.due_date_overridden && <span className="text-amber-500 ml-1">(manually overridden)</span>}</p>
                  )}
                </div>
              )}
            </div>

            {/* Linked Phase */}
            {scheduleItems.length > 0 && (
              <div>
                <Label>Linked Phase</Label>
                <Select value={form.schedule_item_id || 'none'} onValueChange={v => { const val = v === 'none' ? '' : v; setForm(f => ({...f, schedule_item_id: val})); handleUpdate('schedule_item_id', val); }}>
                  <SelectTrigger data-testid="task-detail-phase"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {scheduleItems.map(si => (
                      <SelectItem key={si.id} value={si.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: si.color || '#3B82F6' }} />
                          {si.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            {selectedTask.description && (
              <div>
                <Label>Description</Label>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{selectedTask.description}</p>
              </div>
            )}

            {/* Comments */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3">Comments ({comments.length})</h4>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {comments.map(c => (
                  <div key={c.id} className="text-sm">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium">{c.author_name || 'Unknown'}</span>
                      <span className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-muted-foreground">{c.content}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1" onKeyDown={e => e.key === 'Enter' && handleAddComment()} data-testid="task-comment-input" />
                <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()}>Post</Button>
              </div>
            </div>

            {canEdit && (
              <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedTask.id)} className="w-full" data-testid="delete-task-btn">Delete Task</Button>
            )}
          </div>
        )}
      </AppDrawer>
    </div>
  );
}
